import { Button, Dropdown, Form, Input } from 'semantic-ui-react'
import React, { useState } from 'react'

import { ButtonGroup } from 'components/ButtonGroup'
import styled from 'styled-components'
import useModal from 'hooks/useModal'
import constants from 'config/constants'

interface ViewLayoutButtonProps {
  isCreateView: boolean
  isDisabled: boolean
  onCreateView?: (data: Object, close: () => void) => void
  onUpdateView?: (data: Object, close: () => void) => void
  onSelectResponseView?: () => void
  onSelectAnswerPerRowView?: () => void
  onSelectResponsePerRowView?: () => void
}

export interface ViewLayoutModalProps {
  modal: {
    Header: React.ComponentType
    Content: React.ComponentType
    Actions: React.ComponentType
  }
  isCreateView: boolean
  isDisabled?: boolean
  close: () => void
  onCreateView?: (data: Object, close: () => void) => void
  onUpdateView?: (data: Object, close: () => void) => void
  viewName?: string
  pageLayout?: string | null
}

export const ViewLayoutButton: React.FC<ViewLayoutButtonProps> = ({
  isCreateView,
  isDisabled,
  onCreateView,
  onUpdateView,
}) => {
  const { Modal, open, close, isOpen } = useModal()

  return (
    <React.Fragment>
      <div className="ml-auto" data-html2canvas-ignore="true">
        {isCreateView ? (
          <LinkButton
            onClick={(e) => {
              e.preventDefault()
              open()
            }}
          >
            <span>
              <i className="mx-1 far fa-plus" />
              New feedback view
            </span>
          </LinkButton>
        ) : (
          <React.Fragment>
            <Dropdown.Item className="cursor-pointer" text="Edit view" icon="far fa-edit" onClick={open} />
          </React.Fragment>
        )}
      </div>

      {isOpen && (
        <Modal size="tiny">
          <ViewLayoutModal
            modal={Modal}
            isCreateView={isCreateView}
            isDisabled={isDisabled}
            close={close}
            onCreateView={onCreateView}
            onUpdateView={onUpdateView}
          />
        </Modal>
      )}
    </React.Fragment>
  )
}

export const ViewLayoutModal: React.FC<ViewLayoutModalProps> = ({
  modal,
  isCreateView,
  isDisabled,
  close,
  onCreateView,
  onUpdateView,
  viewName,
  pageLayout = 'response_list',
}) => {
  const [viewNameInput, setViewNameInput] = useState<string>(viewName || '')
  const [layoutType, setLayoutType] = useState<'list' | 'table'>(
    pageLayout === 'response_list' ? 'list' : 'table'
  )
  const [tableType, setTableType] = useState<'answer' | 'response'>(
    pageLayout === 'response_table' ? 'response' : 'answer'
  )

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const pageLayout = layoutType === 'list' ? 'response_list' : tableType + '_table'
    if (isCreateView) {
      onCreateView &&
        onCreateView(
          {
            name: viewNameInput,
            layout: pageLayout,
          },
          close
        )
    } else {
      onUpdateView &&
        onUpdateView(
          {
            name: viewNameInput,
            layout: pageLayout,
          },
          close
        )
    }
  }

  return (
    <React.Fragment>
      <modal.Header>{`${isCreateView ? 'Create new' : 'Edit'} view`}</modal.Header>
      <modal.Content>
        <p>
          <a
            href="https://help.simplesat.io/en/articles/3799899-feedback#feedback-views"
            target="_blank"
            rel="noreferrer"
          >
            <i className="icon far fa-book" />
            Learn more about feedback views
          </a>
        </p>
        <Form id="create-view-form" onSubmit={handleSubmit}>
          <Form.Field>
            <label>Name</label>
            <Input
              value={viewNameInput}
              onChange={(e) => setViewNameInput(e.target.value)}
              required
              placeholder="Feedback view name"
            />
          </Form.Field>
          <Form.Field>
            <label>Layout</label>
            <section className="flex gap-3 space-x-3">
              <LayoutButton
                basic
                className={`${layoutType === 'list' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setLayoutType('list')
                }}
              >
                <img
                  className="w-5"
                  alt="List"
                  src={
                    layoutType === 'list'
                      ? constants.feedbackLayoutLineListActiveIcon
                      : constants.feedbackLayoutLineListIcon
                  }
                />
                List
              </LayoutButton>
              <LayoutButton
                basic
                className={`${layoutType === 'table' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setLayoutType('table')
                }}
              >
                <img
                  className="w-5"
                  alt="Table"
                  src={
                    layoutType === 'table'
                      ? constants.feedbackLayoutLineTableActiveIcon
                      : constants.feedbackLayoutLineTableIcon
                  }
                />
                Table
              </LayoutButton>
            </section>
          </Form.Field>
          <Form.Field
            className={`flex items-center justify-between flex-wrap ${
              layoutType !== 'table' ? 'invisible' : 'visible'
            }`}
          >
            <label>Display answers as</label>
            <ButtonGroup>
              <Button
                active={tableType === 'answer'}
                value={'response'}
                onClick={(e) => {
                  e.preventDefault()
                  setTableType('answer')
                }}
              >
                <div className="flex">
                  <img
                    alt="rows"
                    src={
                      tableType === 'answer'
                        ? constants.feedbackLayoutRowActiveIcon
                        : constants.feedbackLayoutRowIcon
                    }
                    className="mr-1"
                  />
                  Rows
                </div>
              </Button>
              <Button
                active={tableType === 'response'}
                value={'response'}
                onClick={(e) => {
                  e.preventDefault()
                  setTableType('response')
                }}
              >
                <div className="flex">
                  <img
                    alt="columns"
                    src={
                      tableType === 'response'
                        ? constants.feedbackLayoutColumnActiveIcon
                        : constants.feedbackLayoutColumnIcon
                    }
                    className="mr-1"
                  />
                  Columns
                </div>
              </Button>
            </ButtonGroup>
          </Form.Field>
        </Form>
      </modal.Content>
      <modal.Actions>
        <Button onClick={close}>Cancel</Button>
        <Button
          type="submit"
          primary
          form="create-view-form"
          loading={isDisabled}
          disabled={isDisabled}
        >
          {isCreateView ? 'Create view' : 'Save'}
        </Button>
      </modal.Actions>
    </React.Fragment>
  )
}

const LinkButton = styled.a`
  user-select: none;
  font-size: 14px;
  cursor: pointer;
  color: #9b9b9b;
  border: none;
  text-align: left;
  margin-left: 10px;
`

const LayoutButton = styled(Button)`
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0px !important;
  color: rgb(165, 173, 186);
  width: 70px;

  .active {
    box-shadow: 0 0 0 0 rgba(88, 102, 126, 0.12) inset !important;
  }

  > img {
    margin-bottom: 5px;
  }
`



// WEBPACK FOOTER //
// ./src/components/ViewLayoutModal/index.tsx